import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  useGetTenantRoutingSettingsQuery,
  useUpsertTenantRoutingSettingsMutation
} from '../../../redux/api/tenant/tenantApi';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../../../components/TwAutocomplete/TwAutocomplete';
import {
  HAZMAT_TYPES,
  PROVIDER,
  ROUTING_TYPES,
  RoutingConfig
} from '../../../providers/User/User.model';
import TwCheckbox from '../../../components/TwCheckboxes/TwCheckboxes';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import { isEqual } from 'lodash';
import {
  hazmatOptions,
  locationLinkOptions,
  routeTypeOptions,
  routingAppOptions
} from '../../../utils/routingUtils';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';

export const RoutingSettings: React.FC = () => {
  const [routingApp, setRoutingApp] = React.useState<TwAutocompleteOption | null>(null);
  const [routeType, setRouteType] = React.useState<TwAutocompleteOption | null>(null);
  const [hazmatTypes, setHazmatTypes] = React.useState<TwAutocompleteOption[]>([]);
  const [avoidTollRoads, setAvoidTollRoads] = React.useState<boolean>(false);
  const [googleLinks, setGoogleLinks] = React.useState<TwAutocompleteOption | null>(null);
  const [hasChanges, setHasChanges] = React.useState(false);

  const { data: routingSettings } = useGetTenantRoutingSettingsQuery(null);
  const [upsertRoutingSettings] = useUpsertTenantRoutingSettingsMutation();

  useEffect(() => {
    if (routingSettings === null)
      upsertRoutingSettings({
        provider: PROVIDER.PC,
        config: {
          google_links: true
        } as RoutingConfig
      });
    if (routingSettings) {
      const routingApp = routingAppOptions.find((opt) => opt.value === routingSettings.provider);
      const routeType = routeTypeOptions.find(
        (opt) => opt.value === routingSettings.config.preferred_route
      );
      const hazmatTypes = hazmatOptions.filter((opt) =>
        routingSettings.config.hazmat.includes(opt.value as HAZMAT_TYPES)
      );
      const linkType = locationLinkOptions.find(
        (o) => o.value === routingSettings.config.google_links
      );

      setRoutingApp(routingApp || null);
      setRouteType(routeType || null);
      setHazmatTypes(hazmatTypes);
      setAvoidTollRoads(routingSettings.config.avoid_tolls || false);
      setGoogleLinks(linkType || null);
    }
  }, [routingSettings]);

  useEffect(() => {
    if (!routingSettings) return;

    const currentConfig = {
      provider: routingApp?.value,
      config: {
        preferred_route: routeType?.value,
        hazmat: hazmatTypes.map((type) => type.value),
        avoid_tolls: avoidTollRoads,
        google_links: googleLinks?.value
      }
    };

    const settingsChanged =
      currentConfig.provider !== routingSettings.provider ||
      currentConfig.config.preferred_route !== routingSettings.config.preferred_route ||
      !isEqual(currentConfig.config.hazmat, routingSettings.config.hazmat) ||
      currentConfig.config.avoid_tolls !== routingSettings.config.avoid_tolls ||
      currentConfig.config.google_links !== routingSettings.config.google_links;

    setHasChanges(settingsChanged);
  }, [routingApp, routeType, hazmatTypes, avoidTollRoads, routingSettings, googleLinks]);

  const handleApplySettings = (): void => {
    if (routingApp) {
      const config: RoutingConfig = {
        preferred_route: routeType?.value as ROUTING_TYPES,
        hazmat: hazmatTypes.map((type) => type.value as HAZMAT_TYPES),
        avoid_tolls: avoidTollRoads,
        google_links: (googleLinks?.value as boolean) ?? true
      };

      upsertRoutingSettings({
        provider: routingApp.value as PROVIDER,
        config
      });
    }
  };

  const handleDiscardChanges = (): void => {
    if (!routingSettings) return;

    const routeType = routeTypeOptions.find(
      (opt) => opt.value === routingSettings.config.preferred_route
    );
    const hazmatTypes = hazmatOptions.filter((opt) =>
      routingSettings.config.hazmat.includes(opt.value as HAZMAT_TYPES)
    );
    const routingApp = routingAppOptions.find((opt) => opt.value === routingSettings.provider);

    setRoutingApp(routingApp || null);
    setRouteType(routeType || null);
    setHazmatTypes(hazmatTypes);
    setAvoidTollRoads(routingSettings.config.avoid_tolls || false);
    setGoogleLinks(googleLinks || null);
  };

  return (
    <Box className="app-settings__container">
      <Box className="tab__title-wrapper">
        <Typography variant="h5" className="tab__title">
          Mapping settings
        </Typography>
        <Typography variant="subtitle2" fontWeight="500" color="#7e858e">
          Manage your mapping settings
        </Typography>
      </Box>
      <Box className="card-setting__container">
        <>
          <Typography variant="subtitle1" fontWeight="600" color="black">
            Location links
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight="500"
            color="#7e858e"
            sx={{ marginBottom: '18px', marginTop: '4px' }}
          >
            When Dispatcher or Driver clicks a location on an Order or Trip...
          </Typography>
          <TwAutocomplete
            name="location_links"
            optionList={locationLinkOptions}
            value={googleLinks}
            onChange={(_, value): void => setGoogleLinks(value)}
            sortList={false}
          />
        </>
        <Typography variant="subtitle1" fontWeight="600" color="black">
          Default mileage system
        </Typography>
        <Typography
          variant="subtitle2"
          fontWeight="500"
          color="#7e858e"
          sx={{ marginBottom: '18px', marginTop: '4px' }}
        >
          Select your default mileage calculation system in Truckbase
        </Typography>
        <TwAutocomplete
          name="default_mileage_calculator"
          label="Default mileage calculator"
          optionList={routingAppOptions}
          value={routingApp}
          onChange={(_, value): void => setRoutingApp(value)}
        />
        {routingApp?.value === PROVIDER.PC && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '20px',
                marginTop: '10px',
                marginBottom: '16px'
              }}
            >
              <Box sx={{ width: '100%' }}>
                <TwAutocomplete
                  name="preferred_route"
                  label="Preferred route"
                  optionList={routeTypeOptions}
                  value={routeType}
                  onChange={(_, value): void => setRouteType(value)}
                />
                <TwCheckbox
                  label="Avoid toll roads"
                  name="toll_roads"
                  value={avoidTollRoads}
                  checked={avoidTollRoads}
                  onInputChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                    setAvoidTollRoads(e.target.checked)
                  }
                />
              </Box>
              <TwAutocomplete
                name="hazmat_restrictions"
                label="Hazmat restrictions"
                multiple
                disableClearable
                value={hazmatTypes}
                onChange={(_, value): void => {
                  if (Array.isArray(value)) {
                    setHazmatTypes(value);
                  }
                }}
                optionList={hazmatOptions}
                renderOption={(props, option): React.ReactNode => {
                  return <RenderOption key={option.id} props={props} option={option} />;
                }}
              />
            </Box>
            <Typography variant="subtitle1" fontWeight="600" color="black">
              Default truck dimension for PC Miler
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight="500"
              color="#7e858e"
              sx={{ marginBottom: '18px', marginTop: '4px' }}
            >
              This pre-set will be used for trip route by default.{' '}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
                <TwField
                  name="height"
                  label="Height"
                  value={13.5}
                  endAdorment="ft"
                  inputType={TwInputTypes.number}
                  disabled
                  sx={{ width: '210px' }}
                />
                <TwField
                  name="length"
                  label="Length"
                  value={48}
                  endAdorment="ft"
                  inputType={TwInputTypes.number}
                  disabled
                  sx={{ width: '210px' }}
                />
                <TwField
                  name="width"
                  label="Width"
                  value={96}
                  endAdorment="in"
                  inputType={TwInputTypes.number}
                  disabled
                  sx={{ width: '210px' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
                <TwField
                  name="weight"
                  label="Weight"
                  value={80000}
                  endAdorment="lbs"
                  inputType={TwInputTypes.number}
                  disabled
                  sx={{ width: '210px' }}
                />
                <TwField
                  name="axles"
                  label="Axles"
                  value={5}
                  inputType={TwInputTypes.number}
                  disabled
                  sx={{ width: '210px' }}
                />
                <TwField
                  name="max_weight_per_axle"
                  label="Max weight per axle"
                  value={34000}
                  endAdorment="lbs"
                  inputType={TwInputTypes.number}
                  disabled
                  sx={{ width: '210px' }}
                />
              </Box>
            </Box>
          </>
        )}
        {hasChanges && (
          <Box className="account__actions">
            <TwButton type={TwButtonType.secondary} label="Discard" action={handleDiscardChanges} />
            <TwButton
              type={TwButtonType.primary}
              label="Save changes"
              action={handleApplySettings}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
